/* eslint-disable */
import { addDays, eachDayOfInterval, getDay, differenceInMinutes } from 'date-fns';
import { format, toDate, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { pathOr } from 'ramda';
import { v4 } from 'uuid';
import { TFunction } from 'i18next';

import { ScheduleElement, ScheduleItem, ShiftScheduleItem } from '../../graphql/components';
import {
  MyScheduleWeeklyDayProps,
  ScheduleElementType,
  MyScheduleDailyShift,
  ScheduleElementSourceType,
} from './types';
import { defaultAgentDaySettings, HOURS_24 } from '../../scenes/Scheduling/config';
import { formatScheduleElementRGBAColor } from '../../utils/chart';
import { getZonedOrLocalTimezoneOffset } from '../../utils/date';
import { handleEndOfDateRange } from '../../utils/dateRange';

const dateFormat = 'yyyy-MM-dd';

export const transformScheduleElementsData = (
  scheduleItems: ScheduleItem[],
  scheduleElements: ScheduleElementSourceType[],
  dailyShifts: MyScheduleDailyShift[],
  startDateRange: Date,
  endDateRange: Date,
  timezone: string,
  localeObj?: Locale
) => {
  const elements: ScheduleElementType[] = [];
  const days: MyScheduleWeeklyDayProps[] = eachDayOfInterval({ start: startDateRange, end: endDateRange }).map(
    (day, index) => {
      const startOfShift = pathOr(undefined, ['shift', 'dateTimeRange', 'startDateTime'], dailyShifts[index]);
      const endOfShift = pathOr(undefined, ['shift', 'dateTimeRange', 'endDateTime'], dailyShifts[index]);

      const zonedDateTime = zonedTimeToUtc(day, timezone).getTime();

      return {
        date: zonedDateTime,
        startOfShift: startOfShift ? zonedTimeToUtc(startOfShift, timezone).getTime() : null,
        endOfShift: endOfShift ? zonedTimeToUtc(endOfShift, timezone).getTime() : null,
        elements: [] as ScheduleElementType[],
        settings: dailyShifts[index]?.settings || defaultAgentDaySettings,
      };
    }
  );

  // Mapping due to localization differences in position of week days
  const daysIndices: Record<number, number> = {};
  days.forEach((day, index) => {
    daysIndices[zonedTimeToUtc(day.date, timezone).getDay()] = index;
  });

  for (const item of scheduleItems) {
    const element = scheduleElements.find((e: ScheduleElementSourceType) => e.id === item.scheduleElementId);

    const startDateTimeInUTC = new Date(item.dateTimeRange.startDateTime).getTime();

    const endDateTimeInUTC = new Date(item.dateTimeRange.endDateTime).getTime();

    if (!element || differenceInMinutes(endDateTimeInUTC, startDateTimeInUTC) < 1) {
      continue;
    }

    const zonedStartDate = utcToZonedTime(item.dateTimeRange.startDateTime, timezone);
    const zonedStartOfStartDateString = format(zonedStartDate, `${dateFormat}'T'00:00:00.000`, {
      timeZone: timezone,
    });
    const zonedStartOfStartDate = toDate(zonedStartOfStartDateString, { timeZone: timezone });
    const startOfStartDateTimeInUTC = Number(format(zonedStartOfStartDate, 'T', { timeZone: timezone }));

    const zonedStartOfNextDate = addDays(zonedStartOfStartDate, 1);
    const zonedStartOfNextDateString = format(zonedStartOfNextDate, `${dateFormat}'T'00:00:00.000`, {
      timeZone: timezone,
    });
    const startOfNextDate = toDate(zonedStartOfNextDateString, { timeZone: timezone });

    const zonedEndDate = utcToZonedTime(item.dateTimeRange.endDateTime, timezone);
    const zonedStartOfEndDateString = format(zonedEndDate, `${dateFormat}'T'00:00:00.000`, {
      timeZone: timezone,
    });
    const zonedStartOfEndDate = toDate(zonedStartOfEndDateString, { timeZone: timezone });
    const startOfEndDateTimeInUTC = Number(format(zonedStartOfEndDate, 'T', { timeZone: timezone }));

    const zonedStartOfStartDateOffset = getZonedOrLocalTimezoneOffset(zonedStartOfStartDate, timezone);
    const zonedStartDateOffset = getZonedOrLocalTimezoneOffset(zonedStartDate, timezone);
    const zonedStartDateOffsetDeltaInMs = (zonedStartOfStartDateOffset - zonedStartDateOffset) * 60 * 1000;

    const zonedStartOfEndDateOffset = getZonedOrLocalTimezoneOffset(zonedStartOfEndDate, timezone);
    const zonedEndDateOffset = getZonedOrLocalTimezoneOffset(zonedEndDate, timezone);
    const zonedEndDateOffsetDeltaInMs = (zonedStartOfEndDateOffset - zonedEndDateOffset) * 60 * 1000;

    const isDSTChangeDuringElementRange = zonedStartDateOffsetDeltaInMs !== zonedEndDateOffsetDeltaInMs;

    const start =
      startDateTimeInUTC -
      startOfStartDateTimeInUTC +
      (isDSTChangeDuringElementRange ? 0 : zonedStartDateOffsetDeltaInMs);
    let end = endDateTimeInUTC - startOfEndDateTimeInUTC + zonedEndDateOffsetDeltaInMs;

    if (start >= end) {
      /**
       * we need to adjust calculated end value in 2 cases:
       * - when element's duration is 24hrs so the start and end date were calculated as the same values,
       * - when element is part of night shift - starts at first day and ends at the another
       */
      end += HOURS_24;
    }

    const commonElementProps = {
      scheduleElementId: element.id,
      scheduleElementName: element.name,
      scheduleElementNameSize: element.nameSize,
      scheduleElementOriginalName: element.originalName,
      key: v4(),
      color: formatScheduleElementRGBAColor(element.color),
      paid: element.paid,
      paidTimeOff: element.paidTimeOff,
      productive: element.productive,
    };

    const startDayPosition = Number(format(zonedStartDate, 'i', { locale: localeObj, timeZone: timezone })) % 7;
    const endDayPosition =
      Number(format(handleEndOfDateRange(zonedEndDate), 'i', { locale: localeObj, timeZone: timezone })) % 7;

    if (startDayPosition === endDayPosition) {
      const dayPosition = Number(format(zonedStartDate, 'i', { locale: localeObj, timeZone: timezone })) % 7;
      const newElement = {
        start,
        end,
        date: startOfStartDateTimeInUTC,
        custom: { endDateTime: endDateTimeInUTC, startDateTime: startDateTimeInUTC },
        y: daysIndices[dayPosition],
        ...commonElementProps,
      };

      if (days[daysIndices[dayPosition]].date === startOfStartDateTimeInUTC) {
        days[daysIndices[dayPosition]].elements.push(newElement);
      }

      elements.push(newElement);
    } else {
      // push 2 split schedule elements in case of element exists within multiple days
      const firstDayPosition =
        Number(
          format(utcToZonedTime(item.dateTimeRange.startDateTime, timezone), 'i', {
            locale: localeObj,
            timeZone: timezone,
          })
        ) % 7;
      const secondDayPosition =
        Number(
          format(utcToZonedTime(item.dateTimeRange.endDateTime, timezone), 'i', {
            locale: localeObj,
            timeZone: timezone,
          })
        ) % 7;
      const boundaryDateTimeInUTC = startOfNextDate.getTime();

      const firstNewElement = {
        start,
        custom: { startDateTime: startDateTimeInUTC, endDateTime: boundaryDateTimeInUTC },
        date: startOfStartDateTimeInUTC,
        end: HOURS_24,
        y: daysIndices[firstDayPosition],
        ...commonElementProps,
      };
      const secondNewElement = {
        end: end - HOURS_24,
        custom: { endDateTime: endDateTimeInUTC, startDateTime: boundaryDateTimeInUTC },
        date: startOfEndDateTimeInUTC,
        start: 0,
        y: daysIndices[secondDayPosition],
        ...commonElementProps,
      };

      if (days[daysIndices[firstDayPosition]].date === startOfStartDateTimeInUTC) {
        days[daysIndices[firstDayPosition]].elements.push(firstNewElement);
        elements.push(firstNewElement);
      }

      if (days[daysIndices[secondDayPosition]].date === startOfEndDateTimeInUTC) {
        days[daysIndices[secondDayPosition]].elements.push(secondNewElement);
        elements.push(secondNewElement);
      }
    }
  }

  return {
    days,
    elements,
  };
};

export const transformScheduleElementsOnly = (
  scheduleItems: ScheduleItem[] | ShiftScheduleItem[],
  scheduleElements: ScheduleElement[],
  t: TFunction
) => {
  const elements: ScheduleElementType[] = [];

  for (const item of scheduleItems) {
    const element = scheduleElements.find((e: ScheduleElement) => e.id === item.scheduleElementId);
    if (!element) {
      continue;
    }

    const typedItem = item as ScheduleItem;
    const range = typedItem.dateTimeRange
      ? { from: typedItem.dateTimeRange.startDateTime, to: typedItem.dateTimeRange.endDateTime }
      : (item as ShiftScheduleItem).dateRange;

    const start = new Date(range.from).getTime();
    const end = new Date(range.to).getTime();

    const elementName = translateScheduleElementName(t, element);

    const commonElementProps = {
      dayIndex: -1,
      scheduleElementId: element.id,
      scheduleElementName: elementName,
      // size of scheduleElementName is not being used, but we're adding this to be compliant with the provided type
      scheduleElementNameSize: 0,
      scheduleElementOriginalName: element.name,
      key: v4(),
      color: formatScheduleElementRGBAColor(element.color),
      paid: element.paid,
      paidTimeOff: element.paidTimeOff,
      productive: element.productive,
      shift: element.shift,
    };

    const dayPosition = getDay(start);
    const newElement = {
      end,
      start,
      custom: { endDateTime: end, startDateTime: start },
      date: start,
      y: dayPosition,
      ...commonElementProps,
    };

    elements.push(newElement);
  }

  return elements;
};

export const transformSourceScheduleElements = (scheduleElements: ScheduleElement[]) =>
  scheduleElements.map((element) => {
    const splittedElementName = element.name.split(' ');
    const emptyCharsInName = splittedElementName.length - 1;
    const splittedElementNameCharsLength = splittedElementName.join('').length;

    // 10 px for 1 letter, 2px for empty space character
    const approximatedElementNameSize = splittedElementNameCharsLength * 10 + emptyCharsInName * 2;

    return {
      ...element,
      nameSize: approximatedElementNameSize,
    };
  });

export const translateScheduleElementName = (t: TFunction, element?: ScheduleElement) => {
  if (!element) {
    // mainly for test purposes, shouldn't really happen with real data
    return '';
  }

  const nameKey = `schedule.element.default.${element.name}`;
  const translatedName = t(nameKey) as string;
  return translatedName === nameKey ? element.name : translatedName; // custom elements should use non-localized names
};
